import { getLBRules } from 'imports/checkout/api/helpers';
import { getCountry } from 'imports/checkout/api/utils';
import { Push as FaqPush } from 'imports/help/ui/components/Link';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useEffect, useState } from 'react';
import { Push } from '/components/Link';
import env from '/env';
import useIntl from '/imports/core/api/useIntl';
import useTracking from '/imports/core/hooks/useTracking';
import { cookieParser, getDomainFromHost, getJobTrackRoute } from '/lib/helpers';

const LoadingPlaceholder = () => <div></div>;
const RemoveLandingExperiment = dynamic(() => import('/imports/landing/ui/components/RemoveLandingExperiment'), {
  ssr: false,
});
const Homepage = dynamic(() => import('/imports/homepage/ui/pages/Homepage'), {
  ssr: true,
  loading: LoadingPlaceholder,
});
const HomepageV2 = dynamic(() => import('/imports/homepageV2/ui/pages/Homepage'), {
  ssr: true,
  loading: LoadingPlaceholder,
});
const HomepageV3 = dynamic(() => import('/imports/homepageV3/ui/pages/Homepage'), {
  ssr: true,
  loading: LoadingPlaceholder,
});
const HomepageV6 = dynamic(() => import('/imports/homepageV6/ui/page/HomePage'), {
  ssr: true,
  loading: LoadingPlaceholder,
});

const HomepageV7 = dynamic(() => import('/imports/homepageV7/ui/page/HomePage'), {
  ssr: true,
  loading: LoadingPlaceholder,
});

const isProd = env.NODE_ENV === 'production';

const LandingPage = ({ userIdentified, currentUser, host, ...rest }) => {
  const myRules = rest?.pageProps?.rule;
  const router = useRouter();
  const { locale } = useIntl();
  const { trackEvent, resetTracking } = useTracking();
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    const { query } = router;
    if (query?.gclid) {
      const today = new Date();
      const formatted = today.toLocaleDateString('en-GB');
      typeof localStorage !== 'undefined' && localStorage.setItem('gclid', query.gclid);
      typeof localStorage !== 'undefined' && localStorage.setItem('gclidCreationTime', formatted);
    }
    setIsHydrated(true);
  }, []);

  const ctaClick = (target) => {
    const { asPath } = router;

    const destinationV2 = userIdentified ? getJobTrackRoute(host, 'resumes').as : '/onboard/start';

    trackEvent('cta_click', { target });

    const queryString = {
      from: asPath,
    };

    Push(`/onboarding?${qs.stringify(queryString)}`, locale, destinationV2);
  };

  useEffect(() => {
    if (router.query.from === 'blog') {
      trackEvent('blog_exit_hack', { host });
    }

    if (router?.query?.reset) {
      resetTracking();
      Push('/', locale);
    }

    if (typeof window !== 'undefined') {
      const url = window.location.hostname;
      const help = url.split('.');

      if (help.includes('help') && window.location.pathname === '/builder') {
        FaqPush('/', locale);
      }

      window.onpageshow = function () {
        if (cookieParser().token && !userIdentified) {
          window.location.reload();
        }
      };
    }
  }, [router, locale, userIdentified, resetTracking, trackEvent, host]);

  if (!isHydrated) return null;

  const nestedProperties = {
    ctaClick,
    userIdentified,
    currentUser,
    page: router.query.page,
    provider: myRules?.providerIfModalHidden,
  };

  if (host === 'resumizeme') {
    return <HomepageV3 {...nestedProperties} />;
  }

  if (host === 'cvelevator') {
    return (
      <>
        <HomepageV7 {...nestedProperties} />
        <RemoveLandingExperiment />
      </>
    );
  }

  if (host === 'elegantcv') {
    return <Homepage {...nestedProperties} />;
  }

  if (['quantumresume'].includes(host)) {
    return (
      <>
        <HomepageV6 {...nestedProperties} />
        <RemoveLandingExperiment />
      </>
    );
  }

  return (
    <>
      <HomepageV2 {...nestedProperties} />
      <RemoveLandingExperiment />
    </>
  );
};

LandingPage.propTypes = {
  userIdentified: PropTypes.bool,
  locale: PropTypes.string,
  currentUser: PropTypes.object,
  host: PropTypes.string,
};

LandingPage.getInitialProps = async (context) => {
  const getHost = context.req
    ? context.req.headers['x-blog-referrer']
      ? context.req.headers['x-blog-referrer']
      : context.req.headers.host
    : typeof window !== 'undefined'
      ? window.location.host
      : '';

  const host = getDomainFromHost(getHost);
  const country = getCountry();
  const rule = await getLBRules(host, country);

  return { rule, req: context.req?.headers };
};

export default LandingPage;
